import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  chatSupportCreation,
  chatSupportThreads,
  sendSupportMessage,
  closeSupportChat,
  addSupportMessage,
  clearBuyerMessages,
} from "../../features/user/userSlice.js";
import { Avatar, Button, CircularProgress, IconButton } from "@mui/material";
import { FaComments } from "react-icons/fa";
import { Send, Close } from "@mui/icons-material";
import { Form, InputGroup } from "react-bootstrap";
import { timeAgo } from "../../utils/timeAgo.js";
import pusher from "../../utils/pusherClient.js";

const SupportChat = ({ show = false, onHide, seller = { name: "Support" } }) => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const userState = useSelector((state) => state.user);

  const {
    createSupportChatData,
    chatSupportThreadsData,
    chatSupportThreadsLoading,
    sendSupportMessageLoading,
    createSupportChatLoading,
  } = userState;

  const [newMessage, setNewMessage] = useState("");

  // Get the active thread reactively
  const activeThread = useMemo(() => {
    if (createSupportChatData?.id) {
      return {
        ...createSupportChatData,
        messages: createSupportChatData.messages || [],
      };
    }

    const active = chatSupportThreadsData.find((t) => t.is_closed === 0);
    return active ? { ...active, messages: active.messages || [] } : null;
  }, [createSupportChatData, chatSupportThreadsData]);

  // Load threads when chat opens
  useEffect(() => {
    if (show) {
      dispatch(chatSupportThreads());
    }
  }, [show, dispatch]);

  // Pusher subscription for real-time messages
  useEffect(() => {
    if (!show || !activeThread?.id) return;

    const chatId = activeThread.id;
    const channel = pusher.subscribe(`support-chat.${chatId}`);
    channel.bind("SupportMessageSent", (data) => {
      console.log("Pusher event received:", data); // debug
      dispatch(
        addSupportMessage({
          chat_id: chatId,
          message: data.message, // adjust if backend sends nested message object
        })
      );
    });

    return () => {
      channel.unbind_all();
      pusher.unsubscribe(`support-chat.${chatId}`);
    };
  }, [show, activeThread?.id, dispatch]);

  // Start a new chat
  const handleStartChat = async () => {
    try {
      const autoSubject = `Support Chat - ${
        user?.name || "Guest"
      } - ${new Date().toLocaleString()}`;

      const newChat = await dispatch(
        chatSupportCreation({ subject: autoSubject })
      ).unwrap();

      dispatch({ type: "user/setCreateSupportChatData", payload: newChat });

      dispatch(chatSupportThreads());
    } catch (err) {
      console.error("Failed to start chat:", err);
    }
  };

  // Send message (only after chat exists)
  const handleSend = async () => {
    if (!newMessage.trim()) return;

    const activeChatId = createSupportChatData?.id || activeThread?.id;
    if (!activeChatId) return alert("Please start a chat first.");

    try {
      await dispatch(
        sendSupportMessage({ chat_id: activeChatId, message: newMessage })
      ).unwrap();

      dispatch(
        addSupportMessage({
          chat_id: activeChatId,
          message: {
            id: Date.now(),
            sender_type: "marketplace_user",
            message: newMessage,
            created_at: new Date().toISOString(),
          },
        })
      );

      setNewMessage("");
    } catch (err) {
      console.error("Failed to send message:", err);
    }
  };

  const handleCloseChat = async () => {
    const activeChatId = createSupportChatData?.id || activeThread?.id;

    if (activeChatId) {
      try {
        await dispatch(closeSupportChat(activeChatId)).unwrap();
      } catch (err) {
        console.error("Failed to close support chat:", err);
      }
    }

    // Reset chat state in Redux
    dispatch(clearBuyerMessages());
    if (onHide) onHide();
  };

  const isLoading =
    createSupportChatLoading ||
    chatSupportThreadsLoading ||
    sendSupportMessageLoading;

  return (
    <>
      <div className="support-chat-wrapper">
        <div className={`support-chat-container ${show ? "open" : "closed"}`}>
          {/* Header */}
          <div className="support-chat-header">
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Avatar
                sx={{
                  bgcolor: "#0d6efd",
                  color: "#fff",
                  width: 32,
                  height: 32,
                }}
              >
                {seller?.name?.charAt(0) || "S"}
              </Avatar>
              <div>
                <h6 style={{ margin: 0, fontSize: "0.9rem" }}>
                  {seller?.name || "Support"}
                </h6>
              </div>
            </div>
            <IconButton
              size="small"
              sx={{ color: "#000" }}
              onClick={handleCloseChat}
            >
              <Close fontSize="small" />
            </IconButton>
          </div>

          <div
            style={{ height: "1px", backgroundColor: "#ddd", width: "100%" }}
          />

          {/* Body */}
          <div className="support-chat-body-wrapper">
            <div className="support-chat-body">
              {!activeThread && (
                <div
                  style={{
                    padding: "10px",
                    background: "#fff3cd",
                    color: "#856404",
                    borderRadius: "8px",
                    fontSize: "0.8rem",
                    marginBottom: "8px",
                    textAlign: "center",
                  }}
                >
                  💡 Click "Start Chat" to initiate a conversation with the
                  seller.
                  <br />
                  Avoid closing the chat modal directly to prevent losing your
                  conversation.
                </div>
              )}

              {isLoading && (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    padding: "20px",
                  }}
                >
                  <CircularProgress size={24} />
                </div>
              )}

              {!isLoading &&
                (activeThread?.messages || []).map((msg) => {
                  const sender =
                    msg.sender_type === "marketplace_user" ? "user" : "admin";
                  return (
                    <div
                      key={msg.id}
                      className={`d-flex mb-2 ${
                        sender === "user"
                          ? "justify-content-end"
                          : "justify-content-start"
                      }`}
                    >
                      <div
                        className={`p-2 rounded-3 shadow-sm ${
                          sender === "user"
                            ? "bg-primary text-white"
                            : "bg-white text-dark"
                        }`}
                        style={{
                          maxWidth: "75%",
                          fontSize: "0.85rem",
                          backgroundColor:
                            sender === "user" ? "#0d6efd" : "#fff",
                          color: sender === "user" ? "#fff" : "#000",
                        }}
                      >
                        <div>{msg.message}</div>
                        <small
                          className={`d-block mt-1 ${
                            sender === "user" ? "text-light" : "text-muted"
                          }`}
                          style={{ fontSize: "0.7rem" }}
                        >
                          {timeAgo(msg.created_at)}
                        </small>
                      </div>
                    </div>
                  );
                })}
            </div>

            {/* Input */}
            <div className="support-chat-input">
              {!activeThread ? (
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  onClick={handleStartChat}
                  disabled={createSupportChatLoading}
                >
                  {createSupportChatLoading ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    "Start Chat"
                  )}
                </Button>
              ) : (
                <InputGroup>
                  <Form.Control
                    placeholder="Type a message..."
                    className="rounded-pill"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleSend()}
                    style={{ fontSize: "0.85rem" }}
                  />
                  <IconButton
                    color="primary"
                    sx={{ ml: 1 }}
                    onClick={handleSend}
                  >
                    {sendSupportMessageLoading ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      <Send fontSize="small" />
                    )}
                  </IconButton>
                </InputGroup>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Embedded CSS */}
      <style>{`
       @keyframes bounce { 
  0%, 100% { 
    transform: translateY(-50%) translateX(0); 
  } 
  50% { 
    transform: translateY(-50%) translateX(-6px); 
  } 
}

.support-chat-wrapper {
  position: fixed;
  bottom: 250px;  /* default position */
  right: 30px;
  z-index: 1000;
  width: 360px;
}

.support-chat-container {
  position: fixed;
  bottom: 100px; /* default position */
  right: 100px;
  width: 360px;
  max-width: 100%;
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.18);
  transform-origin: right bottom;
  transform: scale(0);
  opacity: 0;
  transition: transform 320ms cubic-bezier(.2, .9, .2, 1), opacity 220ms ease;
  pointer-events: none;
  display: flex;
  flex-direction: column;
  z-index: 1300;
}

.support-chat-container.open {
  transform: scale(1);
  opacity: 1;
  pointer-events: auto;
}

.support-chat-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #f8f9fa;
  padding: 0.5rem 1rem;
  border-top-left-radius: 14px;
  border-top-right-radius: 14px;
}

.support-chat-body-wrapper {
  display: flex;
  flex-direction: column;
  height: 400px;
  border-bottom-left-radius: 14px;
  border-bottom-right-radius: 14px;
  overflow: hidden;
  background: #f9fafb;
}

.support-chat-body {
  flex: 1;
  overflow-y: auto;
  padding: 12px;
}

.support-chat-input {
  padding: 8px;
  border-top: 1px solid #ddd;
  background: #f9fafb;
}

.support-chat-floating-button {
  position: fixed;
  bottom: 190px;
  right: 30px;
  display: flex;
  align-items: center;
  z-index: 1400;
}

.support-chat-floating-label {
  position: absolute;
  right: 70px;
  top: 50%;
  transform: translateY(-50%);
  background: #0d6efd;
  color: #fff;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 0.85rem;
  white-space: nowrap;
  animation: bounce 2s infinite;
  clip-path: polygon(100% 50%, 90% 0, 0 0, 0 100%, 90% 100%);
}

.floating-btn {
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: transform 200ms ease;
  outline: none;
}

.chat-icon {
  color: #fff;
  font-size: 22px;
  transition: transform 240ms ease;
}

.floating-btn.open .chat-icon {
  transform: rotate(45deg);
}

.floating-btn:hover {
  transform: translateY(-2px);
}

/* Media Query for Small Screens (Below 576px) */
@media (max-width: 576px) {
  .support-chat-wrapper {
    right: 12px;  /* Adjust right position */
    bottom: 18px; /* Adjust bottom position */
    left: 0; /* Prevent it from going out of the left edge */
    z-index: 1000;
  }

  .support-chat-container {
    position: absolute;
    width: 90%; /* Make the container width fit well within the screen */
    max-width: 100%; 
    left: 6%; /* Center it horizontally */
    transform: translateX(-50%); /* Centering method */
  }

  .support-chat-header {
    padding: 0.5rem; /* Reduce padding for small screens */
  }

  .floating-btn {
    width: 46px;
    height: 46px;
  }

  .chat-icon {
    font-size: 18px;
  }
}


      `}</style>
    </>
  );
};

export default SupportChat;
